import type {
  AugmentedWarViewerState,
  CellId,
  Element,
  PlayerId,
} from "@cloudnest/augmented-war-engine";
import { useSyncExternalStore } from "react";
import { isPlatformGamePath } from "../../shared/routing/game-path-mode";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
} from "../../shared/services/game-api";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function resolveMode(): GameApiMode {
  if (typeof window === "undefined") return "legacy";
  return isPlatformGamePath("augmented-war", window.location.pathname)
    ? "game-platform"
    : "legacy";
}

function getGameApi(): GameApi {
  const nextMode = resolveMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("augmented-war", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

export type ActionResult = {
  readonly success: boolean;
  readonly error?: string;
};

interface DeclareOptions {
  readonly advance?: boolean;
  readonly attackerUseBastion?: boolean;
}
interface ResolveOptions {
  readonly defenderUseBastion?: boolean;
}

export interface AugmentedWarMultiplayerState {
  readonly viewerState: AugmentedWarViewerState | null;
  readonly lastError: { readonly error: string } | null;
  readonly connection:
    | "idle"
    | "connecting"
    | "open"
    | "reconnecting"
    | "closed";
  readonly lastAppliedSeq: number;
  readonly roomId: string | null;
  /** The engine slot this client controls: host→p1, guest→p2, observer→null. */
  readonly mySlot: PlayerId | null;
  readonly isHost: boolean;
}

interface ViewerStateRow {
  readonly sequenceNumber: number;
  readonly state: Record<string, unknown>;
}

const INITIAL_STATE: AugmentedWarMultiplayerState = {
  viewerState: null,
  lastError: null,
  connection: "idle",
  lastAppliedSeq: 0,
  roomId: null,
  mySlot: null,
  isHost: false,
};

let state: AugmentedWarMultiplayerState = { ...INITIAL_STATE };
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Keep the highest-sequence projected row — the server already redacted it. */
function adoptLatest(rows: readonly ViewerStateRow[], fromSeq: number): number {
  let bestSeq = fromSeq;
  let best: Record<string, unknown> | null = null;
  for (const row of rows) {
    if (row.sequenceNumber > bestSeq) {
      bestSeq = row.sequenceNumber;
      best = row.state;
    }
  }
  if (best !== null) {
    state = {
      ...state,
      viewerState: best as unknown as AugmentedWarViewerState,
      lastAppliedSeq: bestSeq,
    };
  }
  return bestSeq;
}

function setError(error: unknown, fallback: string): void {
  state = {
    ...state,
    lastError: { error: error instanceof Error ? error.message : fallback },
  };
  notify();
}

async function fetchAndAdopt(roomId: string, fromSeq: number): Promise<void> {
  const res = await getGameApi().getViewerStates(roomId, fromSeq);
  if (res.success && res.data && state.roomId === roomId) {
    adoptLatest(res.data, fromSeq);
  }
}

/** Submit one command, then pull the caller's freshly projected viewer state. */
async function submitThenSync(
  roomId: string,
  eventType: string,
  payload: Record<string, unknown>,
): Promise<ActionResult> {
  try {
    const res = await getGameApi().submitEvent(roomId, eventType, payload);
    if (!res.success) {
      const error = res.error ?? "ERR_AUGMENTED_WAR_ACTION";
      state = { ...state, lastError: { error } };
      notify();
      return { success: false, error };
    }
    await fetchAndAdopt(roomId, state.lastAppliedSeq);
    if (state.roomId !== roomId) return { success: true };
    state = { ...state, lastError: null };
    notify();
    return { success: true };
  } catch (error) {
    setError(error, "ERR_AUGMENTED_WAR_ACTION");
    return { success: false, error: "ERR_AUGMENTED_WAR_ACTION" };
  }
}

function withRoom(
  fn: (roomId: string) => Promise<ActionResult>,
): Promise<ActionResult> {
  const roomId = state.roomId;
  if (!roomId) return Promise.resolve({ success: false, error: "ERR_NO_GAME" });
  return fn(roomId);
}

async function beginSession(
  roomId: string,
  mySlot: PlayerId | null,
  isHost: boolean,
): Promise<void> {
  state = {
    ...INITIAL_STATE,
    roomId,
    mySlot,
    isHost,
    connection: "connecting",
  };
  notify();
}

export const augmentedWarMultiplayerActions = {
  initializeAsHost: async (roomId: string): Promise<void> => {
    await beginSession(roomId, "p1", true);
    try {
      const existing = await getGameApi().getViewerStates(roomId);
      const rows = existing.success && existing.data ? existing.data : [];
      if (rows.length > 0) {
        adoptLatest(rows, 0);
      } else {
        // Server builds the authoritative initial state from room config; the
        // Sync then materialises the first per-viewer projections.
        await getGameApi().submitEvent(roomId, "init", {});
        await getGameApi().submitEvent(roomId, "SyncViewerState", {
          actionId: `${roomId}:sync-initial-view`,
        });
        await fetchAndAdopt(roomId, 0);
      }
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_AUGMENTED_WAR_INIT_FAILED");
    }
  },

  initializeAsGuest: async (roomId: string): Promise<void> => {
    await beginSession(roomId, "p2", false);
    try {
      await fetchAndAdopt(roomId, 0);
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_AUGMENTED_WAR_FETCH_FAILED");
    }
  },

  initializeAsObserver: async (roomId: string): Promise<void> => {
    await beginSession(roomId, null, false);
    try {
      await fetchAndAdopt(roomId, 0);
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_AUGMENTED_WAR_FETCH_FAILED");
    }
  },

  replayEvents: async (roomId: string): Promise<void> => {
    try {
      await fetchAndAdopt(roomId, state.lastAppliedSeq);
      notify();
    } catch (error) {
      setError(error, "ERR_AUGMENTED_WAR_REPLAY_FAILED");
    }
  },

  applyRemoteEvent: (
    payloadState: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;
    state = {
      ...state,
      viewerState: payloadState as unknown as AugmentedWarViewerState,
      lastAppliedSeq: seq,
    };
    notify();
  },

  move: (from: CellId, to: CellId): Promise<ActionResult> =>
    withRoom((roomId) => submitThenSync(roomId, "Move", { from, to })),

  deploy: (to: CellId): Promise<ActionResult> =>
    withRoom((roomId) => submitThenSync(roomId, "Deploy", { to })),

  endTurn: (): Promise<ActionResult> =>
    withRoom((roomId) => submitThenSync(roomId, "EndTurn", {})),

  declareAttack: (
    from: CellId,
    target: CellId,
    attackerElement: Element,
    opts: DeclareOptions = {},
  ): Promise<ActionResult> =>
    withRoom((roomId) =>
      submitThenSync(roomId, "DeclareAttack", {
        from,
        target,
        attackerElement,
        advance: opts.advance ?? true,
        ...(opts.attackerUseBastion !== undefined
          ? { attackerUseBastion: opts.attackerUseBastion }
          : {}),
      }),
    ),

  resolveAttack: (
    defenderElement: Element,
    opts: ResolveOptions = {},
  ): Promise<ActionResult> =>
    withRoom((roomId) =>
      submitThenSync(roomId, "ResolveAttack", {
        defenderElement,
        ...(opts.defenderUseBastion !== undefined
          ? { defenderUseBastion: opts.defenderUseBastion }
          : {}),
      }),
    ),

  clearError: (): void => {
    state = { ...state, lastError: null };
    notify();
  },

  reset: (): void => {
    state = { ...INITIAL_STATE };
    notify();
  },
};

export function useAugmentedWarMultiplayerStore(): AugmentedWarMultiplayerState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}
